// Mention tokens in comment bodies: [@u{user_id}|First Last] for an OpsHub user and, since 2026-10-21,
// [@c{contact_id}|Name] for a mention contact (a shared Shopify staff account, Settings → Mention Contacts).
import React from 'react';

export const MENTION_RE = /\[@([uc])(\d+)\|([^\]]+)\]/g;

/** {user_id, name} (an OpsHub user) or {kind: 'u'|'c', id, name}. */
export const kindOf = (x) => (x && x.kind === 'c' ? 'c' : 'u');
export const idOf = (x) => (x.id ?? x.user_id ?? x.contact_id);
export function tokenFor(x) { return `[@${kindOf(x)}${idOf(x)}|${String(x.name || '').replace(/[[\]]/g, '')}]`; }

// Links (Eric, 2026-11-03): Shopify keeps a link pasted into a timeline comment as [link:URL], and the comment
// mirrors into OpsHub with that token. It renders as a clickable link, and so does a bare http(s) address.
export const LINK_RE = /\[link:\s*(https?:\/\/[^\]\s]+)\s*\]|(https?:\/\/[^\s<>\]]+)/g;
const TRAILING_PUNCT = /[.,;:!?)'"]+$/;

export function plainText(body) {
    return (body || '').replace(MENTION_RE, (_, kind, id, name) => `@${name.trim()}`)
                       .replace(/\[link:\s*(https?:\/\/[^\]\s]+)\s*\]/g, '$1');
}

/** Plain text -> array of strings and <a class="cm-link"> nodes. */
function renderLinks(text, keyPrefix) {
    const out = []; let last = 0; let i = 0;
    const re = new RegExp(LINK_RE.source, 'g');
    let m;
    while ((m = re.exec(text)) !== null) {
        let url = m[1] || m[2];
        let end = m.index + m[0].length;
        if (!m[1]) {                       // a bare address: leave a sentence's closing punctuation outside the link
            const tail = (url.match(TRAILING_PUNCT) || [''])[0];
            if (tail) { url = url.slice(0, -tail.length); end -= tail.length; }
        }
        if (m.index > last) out.push(text.slice(last, m.index));
        out.push(<a key={`${keyPrefix}l${i++}`} className="cm-link" href={url} target="_blank" rel="noopener noreferrer">{url}</a>);
        last = end;
        re.lastIndex = end;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** Body -> array of strings, <span class="cm-mention"> nodes and <a class="cm-link"> nodes. */
export function renderMentions(body) {
    const out = []; let last = 0; let i = 0;
    const s = body || '';
    const re = new RegExp(MENTION_RE.source, 'g');
    let m;
    while ((m = re.exec(s)) !== null) {
        if (m.index > last) out.push(...renderLinks(s.slice(last, m.index), `t${i}`));
        out.push(<span key={`m${i++}`} className={m[1] === 'c' ? 'cm-mention cm-mention--contact' : 'cm-mention'}>@{m[3].trim()}</span>);
        last = m.index + m[0].length;
    }
    if (last < s.length) out.push(...renderLinks(s.slice(last), `t${i}`));
    return out;
}

/** The "@query" being typed at `caret`, or null. */
export function activeQuery(text, caret) {
    const before = text.slice(0, caret);
    const m = /(^|\s)@([A-Za-z0-9._-]*)$/.exec(before);
    return m ? { query: m[2], start: caret - m[2].length - 1 } : null;
}

/** Replace the "@query" at the caret with the token for `user`. */
export function insertMention(text, caret, user) {
    const q = activeQuery(text, caret);
    if (!q) return { text, caret };
    const tok = tokenFor(user) + ' ';
    const next = text.slice(0, q.start) + tok + text.slice(caret);
    return { text: next, caret: q.start + tok.length };
}

/** Body -> [{type:'text', text} | {type:'mention', kind, id, name, user_id?}] (inverse of serializeParts). */
export function splitBody(body) {
    const out = []; const s = body || ''; let last = 0;
    const re = new RegExp(MENTION_RE.source, 'g');
    let m;
    while ((m = re.exec(s)) !== null) {
        if (m.index > last) out.push({ type: 'text', text: s.slice(last, m.index) });
        out.push({ type: 'mention', kind: m[1], id: m[2], name: m[3].trim(), ...(m[1] === 'u' ? { user_id: m[2] } : {}) });
        last = m.index + m[0].length;
    }
    if (last < s.length) out.push({ type: 'text', text: s.slice(last) });
    return out;
}

/** Inverse of splitBody. */
export function serializeParts(parts) {
    return parts.map(p => p.type === 'mention' ? tokenFor({ kind: p.kind, id: p.id, name: p.name }) : p.text).join('');
}
